/* eslint-disable jsx-a11y/mouse-events-have-key-events */

import {Group} from '@visx/group';
import {Text} from '@visx/text';
import {sankey as d3Sankey, SankeyGraph, SankeyLink, SankeyNode, SankeyNodeMinimal} from 'd3-sankey';
import React, {Dispatch, SetStateAction, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {newPalette, NewPaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {Margin} from '../../commons/types';
import {SankeyPath} from './sankeyPath';

/*
 * Constants.
 */

const findLoadingColor = (theme: UserTheme) => theme.greys.shade30;

/*
 * Props.
 */

export type SankeyId = string | number;

export interface SankeyProps {
  /** Chart width */
  width: number;
  /** Chart height */
  height: number;
  /** Chart margin */
  margin?: Margin;
  /** Whether the data is being loading or not. */
  isLoading?: boolean;
  /** Data to be rendered */
  data: ReadonlyArray<RawSankeyData>;

  nodeId?: (node: SankeyNodeMinimal<{}, {}>) => SankeyId;

  /** How large are each node separator. Default to 8. */
  nodeWidth?: number;
  /** Vertical padding between each node separator. Default to 4. */
  nodePadding?: number;

  /** Custom label renderer */
  labelRenderer?: (node: SankeyNode<SNodeExtra, SLinkExtra>) => string;

  onHover?: (data: SankeyLink<SNodeExtra, SLinkExtra>, bounds?: DOMRect) => void;
  onUnhover?: Handler;
  onNodeClick?: HandlerOf<SankeyNode<SNodeExtra, SLinkExtra>>;
  onLinkClick?: HandlerOf<SankeyLink<SNodeExtra, SLinkExtra>>;

  /** Allow to set a node or a link as "selected" from outside the component (will apply the hover effect) */
  selectedNodeId?: SankeyId;
  selectedLinkId?: SankeyId;
}

// Use these interfaces to add custom data to the nodes and links
// eslint-disable-next-line @typescript-eslint/naming-convention
export interface SNodeExtra {
  id: SankeyId;
  color?: NewPaletteColorsEnum;
  name?: string;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface SLinkExtra {
  id?: SankeyId;
}

// The nodes passed as props should be of this format:
export interface RawSankeyData {
  id: SankeyId;
  links: ReadonlyArray<RawSankeyDataLinks>;
  name?: string;
  color?: NewPaletteColorsEnum;
}

export interface RawSankeyDataLinks {
  id?: SankeyId;
  targetId: SankeyId;
  value: number;
}

/*
 * Style.
 */

const StyledSvg = styled.svg`
  overflow: visible;
`;

const StyledText = styled(Text)<{$isHighlighted: boolean}>`
  font-size: ${fontSizes.verySmall};
  fill: ${(p) => (p.$isHighlighted ? p.theme.palette.blue.shade40 : p.theme.greys.shade80)};
  font-weight: ${fontWeights.medium};
`;

/*
 * Component.
 */

// For an annoying reason, the typescript behind SankeyLink & SankeyNode makes all the properties optionnal as they're being added dynamically after sankey(data) is called.
// Thus the need for '!' everytime we need to access those properties otherwise TS isn't happy.
export const Sankey = ({
  width,
  height,
  margin = {},
  isLoading,
  data,
  nodeId,
  nodeWidth = 8,
  nodePadding = 4,
  labelRenderer,
  onHover,
  onUnhover,
  onNodeClick,
  onLinkClick,
  selectedNodeId,
  selectedLinkId,
}: SankeyProps) => {
  const {top = 0, left = 0, right = 0, bottom = 0} = margin;

  const size: [number, number] = [width, height];

  const sankey = d3Sankey();
  sankey.size(size);
  sankey.nodeWidth(nodeWidth);
  sankey.nodePadding(nodePadding);

  if (nodeId) {
    sankey.nodeId(nodeId);
  }

  // I didn't implement all options here but here's the full list of possibilities:
  // https://github.com/d3/d3-sankey

  const [highlightLinkIndexes, setHighlightLinkIndexes] = useState<Array<number>>([]);
  const [highlightNode, setHighlightNode] = useState<number>();
  const [sankeyData, setSankeyData] = useState<SankeyGraph<SNodeExtra, SLinkExtra>>();

  useEffect(() => {
    const sData = createSankeyData([...data]);

    sankey(sData);
    setSankeyData(sData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]); // Annoyingly, `sankey()` updates itself so let's not move it into the list of deps here.

  return sankeyData ? (
    <UserThemeConsumer>
      {(theme) => (
        <StyledSvg width={width + left + right} height={height + top + bottom}>
          <defs>{createGradientDefinitions(sankeyData, theme)}</defs>
          <Group top={top} left={left}>
            <Group>
              <Group>
                {sankeyData.links.map((link, i) => (
                  <SankeyPath
                    key={`link-${link.index}`}
                    link={link}
                    i={i}
                    isLoading={isLoading}
                    setHighlightLinkIndexes={setHighlightLinkIndexes}
                    highlightLinkIndexes={highlightLinkIndexes}
                    onClick={onLinkClick}
                    onHover={onHover}
                    onUnhover={onUnhover}
                    selectedLinkId={selectedLinkId}
                  />
                ))}
              </Group>
              {sankeyData.nodes.map((node, i) => (
                <Group top={node.y0} left={node.x0} key={`node-${node.index}`}>
                  {renderSectionBar(node, nodeWidth, i, setHighlightNode, theme, onNodeClick, isLoading)}
                  {!isLoading &&
                    renderLabel(
                      node,
                      highlightNode,
                      setHighlightNode,
                      labelRenderer,
                      onNodeClick,
                      selectedNodeId,
                    )}
                </Group>
              ))}
            </Group>
          </Group>
        </StyledSvg>
      )}
    </UserThemeConsumer>
  ) : null;
};

/*
 * Helpers.
 */

/**
 * Turns the initial data array into the format readible by d3-sankey.
 * i.e. a list of nodes and links
 */
function createSankeyData(originalData: Array<RawSankeyData>): SankeyGraph<SNodeExtra, SLinkExtra> {
  const nodes: Array<SankeyNode<SNodeExtra, SLinkExtra>> = [];
  const links: Array<SankeyLink<SNodeExtra, SLinkExtra>> = [];

  originalData.forEach((dataEntry) => {
    nodes.push({
      id: dataEntry.id,
      name: dataEntry.name,
      color: dataEntry.color,
    });

    dataEntry.links.forEach((link) => {
      const sourceNodeIndex = originalData.findIndex((entry) => entry.id === dataEntry.id);
      const targetNodeIndex = originalData.findIndex((entry) => entry.id === link.targetId);

      if (sourceNodeIndex >= 0 && targetNodeIndex >= 0) {
        links.push({
          id: link.id,
          source: sourceNodeIndex,
          target: targetNodeIndex,
          value: link.value,
        });
      }
    });
  });

  return {
    nodes,
    links,
  };
}

/**
 * Creates all the needed gradients based on the color property of each node.
 * We need one gradient for each path, the start color being the color of the source node, and the end color being the color of the target node.
 */
function createGradientDefinitions<N extends SNodeExtra, L extends SLinkExtra>(
  {nodes, links}: SankeyGraph<N, L>,
  theme: UserTheme,
) {
  const loadingGradient = (
    <linearGradient id="grad-loading" key="loading">
      <stop offset="0%" stopColor={findLoadingColor(theme)} />
      <stop offset="100%" stopColor={findLoadingColor(theme)} />
    </linearGradient>
  );

  return [
    loadingGradient,
    ...links.map((link) => {
      const sourceNode = link.source as SankeyNode<N, L>;
      const targetNode = link.target as SankeyNode<N, L>;

      return (
        <linearGradient
          key={`${sourceNode.index}-${targetNode.index}`}
          id={`grad-${sourceNode.index}-${targetNode.index}`}
        >
          <stop offset="0%" stopColor={nodes[sourceNode.index!].color} />
          <stop offset="100%" stopColor={nodes[targetNode.index!].color} />
        </linearGradient>
      );
    }),
  ];
}

/**
 * Renders the label for each section.
 * Only the first level of depth has their label on the right side, other labels are on the left side of the section.
 */
function renderLabel<N extends SNodeExtra, L extends SLinkExtra>(
  node: SankeyNode<N, L>,
  highlightNode: number | undefined,
  setHighlightNode: Dispatch<SetStateAction<number | undefined>>,
  labelRenderer?: (node: SankeyNode<N, L>) => string,
  onClick?: HandlerOf<SankeyNode<N, L>>,
  selectedNodeId?: SankeyId,
) {
  const textLabel = labelRenderer ? labelRenderer(node) : makeDefaultTextLabel(node.name, node.value);
  return (
    <StyledText
      x={node.depth === 0 ? 10 : -10}
      y={(node.y1! - node.y0!) / 2}
      verticalAnchor="middle"
      textAnchor={node.depth === 0 ? 'start' : 'end'}
      onMouseOver={() => setHighlightNode(node.index)}
      onMouseOut={() => setHighlightNode(undefined)}
      onClick={() => onClick && onClick(node)}
      $isHighlighted={Boolean((highlightNode && highlightNode === node.index) || node.id === selectedNodeId)}
    >
      {textLabel}
    </StyledText>
  );
}

function makeDefaultTextLabel(name?: string, value?: number) {
  const valueLabel = typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : '';
  const nameLabel = name || '';
  return nameLabel ? `${nameLabel} (${valueLabel})` : valueLabel;
}

/**
 * Renders the section vertical bar / separator.
 */
function renderSectionBar<N extends SNodeExtra, L extends SLinkExtra>(
  node: SankeyNode<N, L>,
  nodeWidth: number,
  i: number,
  setHighlightNode: Dispatch<SetStateAction<number | undefined>>,
  theme: UserTheme,
  onClick?: HandlerOf<SankeyNode<N, L>>,
  isLoading?: boolean,
) {
  return (
    <rect
      id={`rect-${i}`}
      width={nodeWidth}
      height={node.y1! - node.y0!}
      fill={
        isLoading
          ? findLoadingColor(theme)
          : (node.color && newPalette[node.color]?.lowlight) || newPalette.blue.lowlight
      }
      onMouseOver={() => !isLoading && setHighlightNode(node.index)}
      onMouseOut={() => !isLoading && setHighlightNode(undefined)}
      onClick={() => !isLoading && onClick && onClick(node)}
    />
  );
}
